
import {defineComponent} from "vue";
import {ProductCategoryRestDto} from "@/api/pharma-cpc-product/models";
import ProductCategoriesTree from "@/components/UI/catalog/ProductCategoriesTree.vue";
import {default as Modal} from "bootstrap/js/dist/modal";
import BaseModal from "@/components/UI/Bootstrap/Modal/BaseModal.vue";
import BaseModalButton from "@/components/UI/Bootstrap/Modal/BaseModalButton.vue";
import SelectCategoriesSelected from "@/components/layouts/catalog/SelectCategoriesSelected.vue";
import {generateDomId} from "@/helpers/functions/string";
import {ProductRestService} from "@/services/rest/cpc-product/ProductRestService";
import {PropType} from "vue/dist/vue";
import {RouteLocationNormalized} from 'vue-router';
import {ProductCategoryRestService} from "@/services/rest/cpc-product-category-mgmt/ProductCategoryRestService";
import {routeEvents} from "@/helpers/routeEvents";

export default defineComponent({
  name: "SelectCategories",
  components: {
    SelectCategoriesSelected,
    BaseModalButton, BaseModal, ProductCategoriesTree
  },
  emits: ["addCategory", "removeCategory"],
  props: {
    selectedProductCategories: {
      type: Array,
      required: true,
    },
    btnClass: {
      type: String,
      required: false,
      default: 'btn btn-icon btn-primary w-100 mb-3'
    },
    btnIcon: {
      type: String,
      required: false,
      default: 'search'
    },
    catalogCustomerCode: {
      type: String,
      required: false,
    },
    excludedCodes: {
      type: Array as PropType<string[]>,
      required: false,
    },
  },
  data() {
    return {
      selectCategoriesModal: null as Modal | null,
      selectCategoriesModalId: generateDomId('select-categories-modal') as string,
      categoriesTree: null as ProductCategoryRestDto | null,
    }
  },
  mounted() {
    routeEvents.on('before-route-change', this.onBeforeRouteChange);
    this.selectCategoriesModal = new Modal('#' + this.selectCategoriesModalId);
    this.reloadContent();
  },
  unmounted() {
    routeEvents.off('before-route-change', this.onBeforeRouteChange);
    this.selectCategoriesModal?.dispose();
  },
  computed: {
    formattedSelectedProductCategories(): string[] {
      let formattedSelectedProductCategories = [] as string[];

      this.selectedProductCategories.forEach((selectedProductCategory: any) => {
        if (typeof selectedProductCategory === 'string') {
          formattedSelectedProductCategories.push(selectedProductCategory);
        } else if (typeof selectedProductCategory === 'object' && 'category_code' in selectedProductCategory) {
          formattedSelectedProductCategories.push(selectedProductCategory.category_code as string);
        }
      });

      return formattedSelectedProductCategories;
    },
    filteredCategoriesTree(): ProductCategoryRestDto | null {
      if (!this.categoriesTree || !this.excludedCodes) {
        return this.categoriesTree;
      }

      return this.filterCategoriesTree(this.categoriesTree);
    }
  },
  methods: {
    getRouteGroup(path: string): string {
      return path.split('/').slice(1, 3).join('/'); // '/catalog/products' or '/catalog/base-prices'
    },
    onBeforeRouteChange(event: { from: RouteLocationNormalized, to: RouteLocationNormalized }) {
      const currentGroup = this.getRouteGroup(event.to.path);
      const previousGroup = event.from?.path ? this.getRouteGroup(event.from.path) : null;

      // Invalidate cache if group changed or first load
      if (previousGroup !== currentGroup) {
        ProductCategoryRestService.getInstance().clearCategoryCache(this.catalogCustomerCode);
      }
    },
    addCategory(category_code: string) {
      this.$emit('addCategory', category_code);
    },
    removeCategory(category_code: string) {
      this.$emit('removeCategory', category_code);
    },
    async reloadContent() {
      this.categoriesTree = await ProductRestService.getInstance().getProductCategoriesTreeCustomer(this.catalogCustomerCode);
    },
    filterCategoriesTree(tree: ProductCategoryRestDto): ProductCategoryRestDto | null {
      if (this.excludedCodes?.includes(tree.current_category.code)) {
        return null;
      }

      const newChildren: ProductCategoryRestDto[] = [];
      tree.subcategories?.forEach((subcategory: ProductCategoryRestDto) => {
        const filteredSubcategory: ProductCategoryRestDto | null = this.filterCategoriesTree(subcategory);
        if (filteredSubcategory) {
          newChildren.push(filteredSubcategory);
        }
      });

      tree.subcategories = newChildren.length > 0 ? newChildren : null;

      return tree;
    }
  }
});
